import { toast } from "@heroui/react";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button, Description, Input, Label, Modal, TextField } from "@heroui/react";
import { buildTree } from "@hs/shared";
import { RacDialogTriggerAnchor } from "@/components/RacDialogTriggerAnchor";
import { trpc } from "@/lib/trpc";
import { TreeSelect, type TreeSelectNode } from "@/components/custom";

const formSchema = z.object({
  name: z.string().min(1, "请输入分类名称").max(100),
  parentId: z.string().nullable().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export type CategoryEditMode = "create" | "update" | "add-child";

export type CategoryFlatItemForTree = {
  id: string;
  name?: string | null;
  parentId?: string | null;
  order?: number;
};

export interface CategoryEditDialogProps {
  open: boolean;
  mode: CategoryEditMode;
  scope: "my" | "shared";
  /** 新建分类时写入的 `categories.type`（默认 MEDIA，知识库等业务传入自有常量） */
  categoryTypeForCreate?: string;
  categoryData?: { id: string; name?: string | null; parentId?: string | null };
  parentCategory?: { id: string; name?: string | null };
  categoryFlatListFromParent?: CategoryFlatItemForTree[];
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void | Promise<void>;
}

type TreeDataLike = { id: string; name?: string | null; children?: TreeDataLike[] };

function toTreeSelectNodes(flat: CategoryFlatItemForTree[], excludeId?: string): TreeSelectNode[] {
  const filtered = excludeId ? flat.filter((c) => c.id !== excludeId) : flat;
  if (filtered.length === 0) return [];
  const tree = buildTree(filtered, {
    parentField: "parentId",
    childrenField: "children",
    sortBy: "order",
    sortOrder: "asc",
  }) as TreeDataLike[];
  const convert = (nodes: TreeDataLike[]): TreeSelectNode[] =>
    nodes.map((node) => ({
      id: node.id,
      label: node.name ?? "",
      children: node.children?.length ? convert(node.children) : undefined,
    }));
  return convert(tree);
}

export function CategoryEditDialog({
  open,
  mode,
  scope,
  categoryTypeForCreate = "MEDIA",
  categoryData,
  parentCategory,
  categoryFlatListFromParent,
  onOpenChange,
  onSuccess,
}: CategoryEditDialogProps) {
  const isUpdate = mode === "update";
  const isAddChild = mode === "add-child";

  const flatList = categoryFlatListFromParent ?? [];

  const parentTreeItems: TreeSelectNode[] = React.useMemo(() => {
    const valid = flatList.filter((c) => c.id != null);
    return toTreeSelectNodes(valid, isUpdate && categoryData?.id ? categoryData.id : undefined);
  }, [flatList, isUpdate, categoryData?.id]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: "", parentId: null },
  });

  const createMutation = trpc.categories.create.useMutation({
    onSuccess: async () => {
      toast.success("新建分类成功");
      form.reset();
      await Promise.resolve(onSuccess());
      onOpenChange(false);
    },
  });

  const updateMutation = trpc.categories.update.useMutation({
    onSuccess: async () => {
      toast.success("保存成功");
      form.reset();
      await Promise.resolve(onSuccess());
      onOpenChange(false);
    },
  });

  React.useEffect(() => {
    if (!open) {
      form.reset();
      return;
    }
    if (isUpdate && categoryData) {
      form.reset({
        name: categoryData.name ?? "",
        parentId: categoryData.parentId ?? null,
      });
    } else if (isAddChild && parentCategory) {
      form.reset({
        name: "",
        parentId: parentCategory.id,
      });
    } else {
      form.reset({ name: "", parentId: null });
    }
  }, [open, isUpdate, isAddChild, categoryData, parentCategory, form]);

  const handleSubmit = async (values: FormValues) => {
    if (isUpdate && categoryData?.id) {
      await updateMutation.mutateAsync({
        id: categoryData.id,
        data: {
          name: values.name,
          parentId: values.parentId ?? undefined,
        },
      });
    } else {
      const payload: {
        name: string;
        type: string;
        parentId?: string | null;
        isSpaceCategory?: boolean;
        isSystemCategory?: boolean;
      } = {
        name: values.name,
        type: categoryTypeForCreate,
        parentId: values.parentId ?? null,
      };
      if (scope === "shared") {
        payload.isSpaceCategory = true;
      }
      await createMutation.mutateAsync(payload);
    }
  };

  const handleModalOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) form.reset();
      onOpenChange(next);
    },
    [form, onOpenChange]
  );

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const title = isUpdate ? "编辑分类" : isAddChild ? "添加下级分类" : "新建分类";
  const errors = form.formState.errors;

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="md">
          <Modal.Dialog className="max-w-md">
            <Modal.Header className="gap-1">
              <Modal.Heading className="text-xl font-medium">{title}</Modal.Heading>
              <Description className="text-sm">填写分类名称与上级（可选）</Description>
            </Modal.Header>
            <Modal.Body className="gap-4 p-1">
              <form id="category-edit-form" className="flex flex-col gap-4" onSubmit={form.handleSubmit(handleSubmit)}>
                <TextField className="w-full" isDisabled={isSubmitting} isInvalid={!!errors.name}>
                  <Label className="text-sm font-medium">
                    名称 <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <Input
                        maxLength={100}
                        placeholder="请输入分类名称"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    )}
                  />
                  {errors.name ? (
                    <Description className="text-danger text-sm">{errors.name.message}</Description>
                  ) : null}
                </TextField>
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium">上级分类</Label>
                  <Controller
                    control={form.control}
                    name="parentId"
                    render={({ field }) => (
                      <TreeSelect
                        aria-label="上级分类"
                        selectionMode="single"
                        items={parentTreeItems}
                        value={field.value ?? null}
                        onChange={(v) => field.onChange(Array.isArray(v) ? v[0] ?? null : v)}
                        placeholder="请选择上级分类"
                        disabled={isSubmitting}
                        emptyMessage="暂无分类"
                      />
                    )}
                  />
                  {errors.parentId ? (
                    <Description className="text-danger text-sm">{errors.parentId.message as string}</Description>
                  ) : null}
                </div>
              </form>
            </Modal.Body>
            <Modal.Footer className="gap-2 sm:justify-end">
              <Button
                type="button"
                variant="secondary"
                onPress={() => {
                  form.reset();
                  onOpenChange(false);
                }}
                isDisabled={isSubmitting}
              >
                取消
              </Button>
              <Button type="submit" form="category-edit-form" isDisabled={isSubmitting}>
                保存
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
